import type { FunctionCall } from '@sisense/sdk-data';
import { DashboardProps, WidgetProps, WidgetType } from '@sisense/sdk-ui';
import { HttpClient } from '@sisense/sdk-rest-client';
import OpenAI from 'openai';
import { z } from 'zod';
import {
	ArtifactReference,
	BaseChartSummary,
	ChartSummary,
} from '../build-chart/types';

export type ExtendedDashboardProps = DashboardProps & ArtifactReference;

/**
 * Chart list summary for LLM context
 */
export type ChartListSummary = {
	message: string;
	createdAt: string;
	chartListId: string;
};

/**
 * Base dashboard summary for LLM consumption and asset extraction
 */
export type BaseDashboardSummary = {
	/** Fusion dashboard ID */
	fusionDashboardId?: string;
	/** Human-readable title of the dashboard */
	dashboardTitle?: string;
	/** Default data source title for the dashboard */
	defaultDataSourceTitle: string;
	/**
	 * Dashboard-scope (Fusion common) filters as NLQ compose-tree nodes.
	 * Empty when none; widget-level chart filters live on {@link BaseDashboardSummary.widgets} `chartState`.
	 */
	filters: FunctionCall[];
	/** Array of widget summaries */
	widgets: Array<{
		/** Widget ID */
		widgetId: string;
		/** Widget title (for chart and pivot table widgets) */
		widgetTitle?: string;
		/** Widget type (for chart and pivot table widgets) */
		widgetType: WidgetType;
		/**
		 * Data source title when it differs from {@link BaseDashboardSummary.defaultDataSourceTitle}.
		 */
		dataSourceTitle?: string;
		/**
		 * Chart widgets only — compact chart JSON (includes chart-level `filters` when present).
		 */
		chartState?: BaseChartSummary;
	}>;
};

/**
 * Summary of a dashboard for LLM consumption and asset extraction
 */
export interface DashboardSummary {
	/** Status message indicating success or failure */
	message: string;
	/** Date and time ISO string of when the dashboard was created */
	createdAt: string;
	/** Unique identifier for the dashboard (typically the tool call ID) */
	dashboardId: string;
	/** Dashboard details, undefined if dashboard creation failed */
	dashboard?: BaseDashboardSummary;
}

/**
 * Row in the `dashboards` array of the build-dashboard system prompt: either a
 * full {@link DashboardSummary} from the host or a single targeted entry by id.
 */
export type DashboardContextPromptEntry =
	| DashboardSummary
	| { dashboardId: string; dashboard: BaseDashboardSummary };

/**
 * Canonical tool description for buildDashboard — reusable by any host (sdk-assistant, MCP server, custom integrations).
 * Hosts may append host-specific notes but should not duplicate or contradict this text.
 */
export const buildDashboardToolDescription = `Creates or updates a dashboard, its chart widgets, and its filters from a natural language prompt.

priorDashboardId: pass the dashboardId of an existing dashboard when updating it. The engine routes all internal tool calls to that dashboard automatically. Omit when creating a new dashboard from scratch.

Chart widgets inside the dashboard support the same structural and display revision capabilities as standalone charts. When updating an existing widget, the engine sets priorChartId internally — no manual wiring needed.

Pass the user prompt exactly as received — do not transform or modify it. The engine handles prompt splitting internally.`;

/**
 * Schema for the main buildDashboard smart tool
 */
export const buildDashboardSchema = z.object({
	dataSourceTitle: z.string().describe('Data source title'),
	userPrompt: z.string().describe(
		`Combined natural language user prompt describing actions on a dashboard.
		
Examples:
- "Create a dashboard with sales charts"
- "Load dashboard ABC123"
- "Add revenue chart to the current dashboard"
- "Rename the dashboard to 'Q4 Sales Report'"
- "Add filters for region and date to the dashboard"
`,
	),
	priorDashboardId: z
		.string()
		.optional()
		.describe(
			'Pass the dashboardId of the existing dashboard being updated. ' +
				'Omit when creating a new dashboard.',
		),
});

export type BuildDashboardParams = z.infer<typeof buildDashboardSchema>;

/**
 * Schema for createDashboard tool
 */
export const createDashboardSchema = z.object({
	dashboardTitle: z.string().optional().describe('Optional dashboard title'),
	widgetIdsToAdd: z
		.array(z.string())
		.describe(
			'Chart Widget IDs or Chart Widget List ID to add to the dashboard. Use empty array if no charts are found',
		),
});

export type CreateDashboardParams = z.infer<typeof createDashboardSchema>;

/**
 * Schema for loadExistingDashboard tool
 */
export const loadExistingFusionDashboardSchema = z.object({
	dashboardTitle: z.string().describe('Fusion dashboard title to load'),
});

export type LoadExistingFusionDashboardParams = z.infer<
	typeof loadExistingFusionDashboardSchema
>;

/**
 * Schema for updateDashboard tool
 */
export const updateDashboardSchema = z.object({
	dashboardId: z.string().describe('Dashboard ID to update'),
	dashboardTitle: z.string().describe('New dashboard title'),
});

export type UpdateDashboardParams = z.infer<typeof updateDashboardSchema>;

/**
 * Schema for addDashboardWidgets tool
 */
export const addDashboardWidgetsSchema = z.object({
	dashboardId: z.string().describe('Existing dashboard ID to add widgets to'),
	widgetIdsToAdd: z
		.array(z.string())
		.describe('Chart IDs or Chart List ID to add to the dashboard'),
});

export type AddDashboardWidgetsParams = z.infer<
	typeof addDashboardWidgetsSchema
>;

/**
 * Schema for createChartForDashboard tool
 */
export const createChartWidgetForDashboardSchema = z.object({
	dashboardId: z.string().describe('Existing dashboard ID to add a chart to'),
	userPrompt: z
		.string()
		.describe('User prompt to describe a chart widget for the dashboard'),
});

export type CreateChartWidgetForDashboardParams = z.infer<
	typeof createChartWidgetForDashboardSchema
>;

/**
 * Schema for updateChartWidgetForDashboard tool
 */
export const updateChartWidgetForDashboardSchema = z.object({
	dashboardId: z
		.string()
		.describe('Existing dashboard ID to update a chart widget in'),
	widgetId: z.string().describe('Widget ID to update'),
	userPrompt: z
		.string()
		.describe('User prompt to describe the update to the chart widget'),
});

export type UpdateChartWidgetForDashboardParams = z.infer<
	typeof updateChartWidgetForDashboardSchema
>;

/**
 * Schema for addDashboardFilters tool
 */
export const addDashboardFiltersSchema = z.object({
	dashboardId: z.string().describe('Dashboard ID to add filters to'),
	userPrompt: z
		.string()
		.describe('Combined user prompt to add filters to the dashboard.'),
});

export type AddDashboardFiltersParams = z.infer<
	typeof addDashboardFiltersSchema
>;

/**
 * Build dashboard context interface
 */
export interface BuildDashboardContext {
	/**
	 * Tool call ID - REQUIRED
	 */
	toolCallId: string;
	/**
	 * Data source title (required)
	 */
	dataSourceTitle: string;
	/**
	 * Chart summaries for context
	 */
	chartSummaries?: ChartSummary[];
	/**
	 * Chart list summaries for context
	 */
	chartListSummaries?: ChartListSummary[];
	/**
	 * Dashboard summaries for context
	 */
	dashboardSummaries?: DashboardSummary[];
	/**
	 * Retrieve a dashboard by ID (optional)
	 * @param dashboardId - The ID of the dashboard to retrieve
	 * @returns The dashboard props or null
	 */
	retrieveDashboard?: (dashboardId: string) => ExtendedDashboardProps | null;
	/**
	 * Save dashboard props (optional)
	 * @param dashboardProps - The dashboard props to save
	 */
	saveDashboard?: (dashboardProps: ExtendedDashboardProps) => void;
	/**
	 * Get widget list by IDs (optional)
	 * @param widgetIds - The IDs of widgets to retrieve
	 * @returns Array of widget props
	 */
	getWidgetList?: (widgetIds: string[]) => WidgetProps[];
	/**
	 * Whether NLQ V3 is enabled
	 */
	isNlqV3Enabled?: boolean;
	httpClient?: HttpClient;
	openAIClient?: OpenAI;
}
